import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AceTranslatePipe, AppLanguageService, BreadcrumbComponent, ButtonComponent, Tablev2Component, TranslationService } from '@ace/ace';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { AssessmentDto, CourseDto, ExamCorrectionLearnerCaseDto, InstructorDto } from '../../models/instructor.models';
import { AssessmentService } from '../../services/assessment.service';
import { CourseService } from '../../services/course.service';
import { InstructorService } from '../../services/instructor.service';
import { MetricCardsComponent } from '../../shared/components/metric-cards/metric-cards.component';
import { formatBackendDateTime } from '../../shared/utils/date-time-display.util';
import { withTranslatedSeverityInfo } from '../../shared/utils/status-severity.util';

@Component({
  selector: 'app-exam-corrections-detail',
  standalone: true,
  imports: [CommonModule, AceTranslatePipe, BreadcrumbComponent, ButtonComponent, Tablev2Component, MetricCardsComponent],
  templateUrl: './exam-corrections-detail.component.html',
  styleUrl: './exam-corrections-detail.component.scss',
})
export class ExamCorrectionsDetailComponent implements OnInit {
  assessmentId = '';
  role: 'FIRST_MARKER' | 'SECOND_MARKER' = 'FIRST_MARKER';
  assessment: AssessmentDto | null = null;
  instructor: InstructorDto | null = null;
  learners: ExamCorrectionLearnerCaseDto[] = [];
  loading = false;
  assessmentType: 'QUIZ' | 'EXAM' | 'ASSIGNMENT' = 'EXAM';
  learnersTableHeader: any[] = [];
  learnersTableData: any[] = [];
  private appLocale: 'en' | 'ar' | 'fr' | 'de' = 'en';
  private coursesById = new Map<string, CourseDto>();

  readonly workflowStatusSeverity = [
    { value: 'SUBMITTED', severity: 'yellow' },
    { value: 'Submitted', severity: 'yellow' },
    { value: 'APPROVED', severity: 'yellow' },
    { value: 'Approved', severity: 'yellow' },
    { value: 'WAITING_FOR_APPROVAL', severity: 'yellow' },
    { value: 'Waiting For Approval', severity: 'yellow' },
    { value: 'Waiting for Approval', severity: 'yellow' },
    { value: 'PARTIALLY_GRADED', severity: 'yellow' },
    { value: 'Partially Graded', severity: 'yellow' },
    { value: 'PASSED', severity: 'yellow' },
    { value: 'Passed', severity: 'yellow' },
    { value: 'FAILED', severity: 'yellow' },
    { value: 'Failed', severity: 'yellow' },
    { value: 'IN_PROGRESS', severity: 'yellow' },
    { value: 'In Progress', severity: 'yellow' },
    { value: 'COMPLETED', severity: 'yellow' },
    { value: 'Completed', severity: 'yellow' },
  ];

  readonly actionStateSeverity = [
    { value: 'NEEDS_YOUR_REVIEW', severity: 'yellow' },
    { value: 'Needs Your Review', severity: 'yellow' },
    { value: 'REVIEWED_BY_YOU', severity: 'yellow' },
    { value: 'Reviewed By You', severity: 'yellow' },
    { value: 'Reviewed by You', severity: 'yellow' },
    { value: 'WAITING_FOR_OTHER_MARKER', severity: 'yellow' },
    { value: 'Waiting For Other Marker', severity: 'yellow' },
    { value: 'Waiting for Other Marker', severity: 'yellow' },
    { value: 'WAITING_FOR_ADMIN', severity: 'yellow' },
    { value: 'Waiting For Admin', severity: 'yellow' },
    { value: 'Waiting for Admin', severity: 'yellow' },
    { value: 'WAITING_FOR_ADMIN_REVIEW', severity: 'yellow' },
    { value: 'Waiting For Admin Review', severity: 'yellow' },
    { value: 'Waiting for Admin Review', severity: 'yellow' },
    { value: 'READ_ONLY', severity: 'yellow' },
    { value: 'Read Only', severity: 'yellow' },
  ];

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly assessmentService: AssessmentService,
    private readonly courseService: CourseService,
    private readonly instructorService: InstructorService,
    private readonly translationService: TranslationService,
    private readonly appLanguageService: AppLanguageService,
  ) {}

  ngOnInit(): void {
    this.appLocale = this.normalizeLanguage(localStorage.getItem('appLang'));
    this.appLanguageService.selectedLanguage$.subscribe((language: any) => {
      this.appLocale = this.normalizeLanguage(language?.isoCode ?? language);
      this.initializeLearnersTableHeader();
      this.rebuildLearnersTableData();
    });
    this.initializeLearnersTableHeader();
    this.assessmentType = this.resolveAssessmentTypeFromRoute();
    this.assessmentId = this.route.snapshot.paramMap.get('id') ?? '';
    const roleParam = this.route.snapshot.queryParamMap.get('role');
    if (roleParam === 'SECOND_MARKER') this.role = 'SECOND_MARKER';
    this.loading = true;
    this.instructorService.getMe().subscribe({
      next: (instructor) => {
        this.instructor = instructor;
        const correctionInstructorId = instructor.id || instructor.userId;
        forkJoin({
          assessments: this.assessmentService.getAll().pipe(
            catchError(() => of([] as AssessmentDto[])),
          ),
          learners: this.assessmentService.getCorrectionCases(correctionInstructorId, this.assessmentId)
            .pipe(catchError(() => of([]))),
          courses: this.courseService.getAll().pipe(
            catchError(() => of([] as CourseDto[])),
          ),
        }).subscribe(({ assessments, learners, courses }) => {
          this.assessment = assessments.find((assessment) => assessment.id === this.assessmentId) ?? null;
          this.learners = learners;
          this.coursesById = new Map(courses.map((course) => [course.id, course]));
          this.rebuildLearnersTableData();
          this.loading = false;
        });
      },
      error: () => {
        this.rebuildLearnersTableData();
        this.loading = false;
      },
    });
  }

  get learnersNeedingReview(): number {
    return this.visibleLearners.length;
  }

  get reviewedByYou(): number {
    return this.learners.filter((learner) => learner.actionState !== 'Needs Your Review').length;
  }

  get assessmentTypeLabel(): string {
    if (this.assessmentType === 'QUIZ') return this.t('Quiz');
    if (this.assessmentType === 'ASSIGNMENT') return this.t('Homework');
    return this.t('Exam');
  }

  get assessmentDisplayName(): string {
    const native = this.assessment?.nameNative?.trim() ?? '';
    const english = this.assessment?.nameEn?.trim() ?? '';
    return this.shouldUseNativeNames() && native ? native : (english || native || this.t('Assessment Overview'));
  }

  get assessmentCourseDisplayName(): string {
    const course = this.assessment?.courseId ? this.coursesById.get(this.assessment.courseId) : undefined;
    const native = course?.courseNameNative?.trim() ?? '';
    const english = course?.courseNameEn?.trim() ?? this.assessment?.courseName?.trim() ?? '';
    return this.shouldUseNativeNames() && native ? native : (english || native || '-');
  }

  get roleLabel(): string {
    return this.role === 'SECOND_MARKER'
      ? this.t('Second Marker')
      : this.t('First Marker');
  }

  get roleDescription(): string {
    return this.role === 'SECOND_MARKER'
      ? this.t('Second marking assigned to you')
      : this.t('Primary grading review assigned to you');
  }

  get scheduledDisplay(): string {
    return formatBackendDateTime(this.assessment?.scheduledStartAt, this.t('Not scheduled'));
  }

  get durationDisplay(): string {
    if (!this.assessment?.timeLimitMinutes) return this.t('No time limit');
    return `${this.assessment.timeLimitMinutes} ${this.t('minutes')}`;
  }

  get totalMarksDisplay(): string {
    return `${this.assessment?.totalMarks ?? 0}`;
  }

  get passMarkDisplay(): string {
    return this.assessment?.passMark != null ? `${this.assessment.passMark}` : '—';
  }

  get questionCountDisplay(): string {
    return this.assessment?.questionCount != null ? `${this.assessment.questionCount}` : '—';
  }

  get assessmentMetricCards(): Array<{ label: string; value: string; description: string; icon: string; color: string }> {
    return [
      {
        label: this.t('Total Marks'),
        value: this.totalMarksDisplay,
        description: this.t('Assessment total score'),
        icon: 'ri-medal-line',
        color: '#333fff',
      },
      {
        label: this.t('Pass Mark'),
        value: this.passMarkDisplay,
        description: this.t('Minimum score to pass'),
        icon: 'ri-check-double-line',
        color: '#16a34a',
      },
      {
        label: this.t('Questions'),
        value: this.questionCountDisplay,
        description: this.t('Questions included'),
        icon: 'ri-questionnaire-line',
        color: '#7c3aed',
      },
      {
        label: this.t('Needs Review'),
        value: `${this.learnersNeedingReview}`,
        description: this.t('Learners waiting for your grading'),
        icon: 'ri-edit-2-line',
        color: '#f59e0b',
      },
    ];
  }

  get markerNameDisplay(): string {
    if (!this.assessment) return '—';
    return this.role === 'SECOND_MARKER'
      ? (this.assessment.secondMarkerName || '—')
      : (this.assessment.firstMarkerName || this.assessment.instructorName || '—');
  }


  roleBadgeClass(role: string): string {
    return role === 'SECOND_MARKER'
      ? 'role-badge role-badge--secondary'
      : 'role-badge role-badge--primary';
  }

  // get pageTitle(): string {
  //   return this.assessment?.nameEn || this.t('Assessment Marking');
  // }

  get visibleLearners(): ExamCorrectionLearnerCaseDto[] {
    return this.learners.filter((learner) => this.isNeedsYourReview(learner));
  }

  openLearnerCase(learner: ExamCorrectionLearnerCaseDto): void {
    void this.router.navigate([this.correctionsBasePath, this.assessmentId, 'submissions', learner.resultId], {
      queryParams: { role: learner.markerRole, fromCorrections: true },
    });
  }

  goBack(): void {
    void this.router.navigate([this.correctionsBasePath]);
  }

  get correctionsBasePath(): string {
    return '/main/assessment/marking';
  }

  get correctionsLabel(): string {
    return this.t('Assessment Marking');
  }

  private initializeLearnersTableHeader(): void {
    const t = (key: string) => this.t(key);
    this.learnersTableHeader = [
      { fieldName: 'rowNumber', displayName: t('No.'), property: 'rowNumber', canExport: false, canSort: false },
      { property: 'learnerName', displayName: t('Learner'), canExport: true },
      { property: 'submittedDisplay', displayName: t('Submitted'), canExport: true },
      {
        property: 'workflowStatusDisplay',
        displayName: t('Status'),
        canExport: true,
        severityInfo: withTranslatedSeverityInfo(this.workflowStatusSeverity, t),
      },
      {
        property: 'actionStateDisplay',
        displayName: t('Review State'),
        canExport: true,
        severityInfo: withTranslatedSeverityInfo(this.actionStateSeverity, t),
      },
      { property: 'tblAction', displayName: t('Action'), canExport: false },
    ];
  }

  private rebuildLearnersTableData(): void {
    this.learnersTableData = this.visibleLearners.map((learner, index) => ({
      ...learner,
      rowNumber: index + 1,
      learnerName: learner.learnerDisplayNameEn || learner.learnerId || '-',
      submittedDisplay: formatBackendDateTime(learner.submittedAt, '-'),
      workflowStatusDisplay: this.toDisplayValue(this.resolveWorkflowStatus(learner)),
      actionStateDisplay: this.toDisplayValue(this.resolveActionState(learner), true),
      tblAction: true,
    }));
  }

  private resolveAssessmentTypeFromRoute(): 'QUIZ' | 'EXAM' | 'ASSIGNMENT' {
    let snapshot: import('@angular/router').ActivatedRouteSnapshot | null = this.route.snapshot;
    while (snapshot) {
      const value = snapshot.data?.['assessmentType'];
      if (value === 'QUIZ' || value === 'EXAM' || value === 'ASSIGNMENT') {
        return value;
      }
      snapshot = snapshot.parent;
    }
    return 'EXAM';
  }

  private t(key: string): string {
    return this.translationService.translate(key);
  }

  shouldUseNativeNames(): boolean {
    return this.appLocale === 'ar';
  }

  private normalizeLanguage(language: unknown): 'en' | 'ar' | 'fr' | 'de' {
    const value = String(language ?? '').toLowerCase();
    if (value.startsWith('ar')) return 'ar';
    if (value.startsWith('fr')) return 'fr';
    if (value.startsWith('de')) return 'de';
    return 'en';
  }

  private resolveWorkflowStatus(learner: ExamCorrectionLearnerCaseDto): string | null {
    const rawLearner = learner as unknown as Record<string, unknown>;
    return this.firstStringValue(rawLearner, [
      'workflowStatus',
      'status',
      'workflowState',
      'workflow_status',
      'statusDisplay',
      'workflowStatusDisplay',
      'learnerStatus',
    ]);
  }

  private resolveActionState(learner: ExamCorrectionLearnerCaseDto): string | null {
    const rawLearner = learner as unknown as Record<string, unknown>;
    return this.firstStringValue(rawLearner, [
      'actionState',
      'reviewState',
      'reviewStatus',
      'markerReviewState',
      'approvalState',
      'adminReviewState',
      'correctionState',
      'action_state',
      'review_state',
      'review_status',
      'marker_review_state',
      'approval_state',
      'admin_review_state',
      'correction_state',
      'workflowState',
    ]);
  }

  private isNeedsYourReview(learner: ExamCorrectionLearnerCaseDto): boolean {
    const actionState = this.resolveActionState(learner);
    return actionState === 'Needs Your Review' || actionState === 'NEEDS_YOUR_REVIEW';
  }

  private firstStringValue(
    source: Record<string, unknown>,
    keys: string[],
  ): string | null {
    for (const key of keys) {
      const value = source[key];
      if (typeof value === 'string' && value.trim().length > 0) {
        return value.trim();
      }
    }
    return null;
  }

  private toDisplayValue(value?: string | null, translate = false): string {
    if (!value) return '—';
    const normalized = value.includes('_') ? this.toDisplayLabel(value) : value;
    return translate ? this.t(normalized) : normalized;
  }

  private toDisplayLabel(value?: string | null): string {
    if (!value) return '—';
    return value
      .split('_')
      .map((part) => part.charAt(0) + part.slice(1).toLowerCase())
      .join(' ');
  }
}
